import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { AdminCustomersPanel } from '@components/admin/AdminCustomersPanel';
import { AdminDownloadsPanel } from '@components/admin/AdminDownloadsPanel';
import { AdminApiError, adminAuth } from '@services/admin/adminAuth';
export const AdminPage = () => {
    const { t, i18n } = useTranslation();
    const [phase, setPhase] = useState('loading');
    const [identifier, setIdentifier] = useState('');
    const [password, setPassword] = useState('');
    const [totp, setTotp] = useState('');
    const [recoveryCode, setRecoveryCode] = useState('');
    const [recoveryMode, setRecoveryMode] = useState(false);
    const [user, setUser] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const errorRef = useRef(null);
    const identifierRef = useRef(null);
    const totpRef = useRef(null);
    const recoveryRef = useRef(null);
    const location = useLocation();
    const navigate = useNavigate();
    const intendedPath = useRef(location.pathname === '/admin/customers' || location.pathname === '/admin/downloads'
        ? location.pathname
        : '/admin');
    useEffect(() => {
        document.title = t('admin.metaTitle');
    }, [t, i18n.language]);
    useEffect(() => {
        let current = true;
        void adminAuth
            .me()
            .then(({ user: currentUser }) => {
            if (!current)
                return;
            setUser(currentUser);
            setPhase('authenticated');
        })
            .catch(() => {
            if (!current)
                return;
            setPhase('login');
            if (intendedPath.current !== '/admin')
                navigate('/admin', { replace: true });
        });
        return () => {
            current = false;
        };
    }, [navigate]);
    useEffect(() => {
        if (error)
            errorRef.current?.focus();
    }, [error]);
    useEffect(() => {
        if (phase === 'login')
            identifierRef.current?.focus();
        if (phase === 'second-factor') {
            (recoveryMode ? recoveryRef : totpRef).current?.focus();
        }
    }, [phase, recoveryMode]);
    const messageFor = (failure) => failure instanceof AdminApiError && failure.status === 429
        ? t('admin.auth.errors.rateLimited')
        : t('admin.auth.errors.generic');
    const submitPassword = async (event) => {
        event.preventDefault();
        if (busy)
            return;
        setError('');
        setBusy(true);
        try {
            await adminAuth.login(identifier, password);
            setPassword('');
            setPhase('second-factor');
        }
        catch (failure) {
            setPassword('');
            setError(messageFor(failure));
        }
        finally {
            setBusy(false);
        }
    };
    const submitSecondFactor = async (event) => {
        event.preventDefault();
        if (busy)
            return;
        setError('');
        setBusy(true);
        try {
            const result = recoveryMode
                ? await adminAuth.useRecoveryCode(recoveryCode)
                : await adminAuth.verifyTotp(totp);
            setTotp('');
            setRecoveryCode('');
            setUser(result.user);
            setPhase('authenticated');
            navigate(intendedPath.current, { replace: true });
        }
        catch (failure) {
            setTotp('');
            setRecoveryCode('');
            setError(messageFor(failure));
        }
        finally {
            setBusy(false);
        }
    };
    const logout = async () => {
        if (busy)
            return;
        setBusy(true);
        setError('');
        try {
            await adminAuth.logout();
            setUser(null);
            setIdentifier('');
            setPhase('login');
            navigate('/admin', { replace: true });
        }
        catch {
            setError(t('admin.auth.errors.logout'));
        }
        finally {
            setBusy(false);
        }
    };
    const isCustomers = location.pathname === '/admin/customers';
    const isDownloads = location.pathname === '/admin/downloads';
    return (_jsxs(PrivateShell, { children: [_jsxs(PrivateHeader, { children: [_jsxs(Brand, { "aria-label": t('admin.brandLabel'), children: [_jsx(BrandMark, { "aria-hidden": "true", children: "AI" }), _jsx("span", { children: "AITechCare" })] }), phase === 'authenticated' && user ? (_jsxs(AdminNavigation, { "aria-label": t('admin.navigation.label'), children: [_jsx(AdminNavLink, { to: "/admin", end: true, children: t('admin.navigation.home') }), _jsx(AdminNavLink, { to: "/admin/customers", children: t('admin.navigation.customers') }), user.role !== 'read_only' && (_jsx(AdminNavLink, { to: "/admin/downloads", children: t('admin.navigation.downloads') })), _jsx(LogoutButton, { type: "button", onClick: () => void logout(), disabled: busy, children: busy ? t('admin.auth.loggingOut') : t('admin.navigation.logout') })] })) : (_jsx(AreaLabel, { children: t('admin.areaLabel') }))] }), _jsxs(Content, { id: "main-content", tabIndex: -1, "$wide": phase === 'authenticated' && (isCustomers || isDownloads), children: [phase === 'loading' && (_jsxs(Card, { "aria-live": "polite", children: [_jsx(Eyebrow, { children: t('admin.auth.restricted') }), _jsx(Title, { children: t('admin.auth.checking') })] })), phase === 'login' && (_jsxs(Card, { children: [_jsx(Eyebrow, { children: t('admin.auth.restricted') }), _jsx(Title, { children: t('admin.auth.loginTitle') }), _jsx(Lead, { children: t('admin.auth.loginLead') }), error && (_jsx(ErrorMessage, { ref: errorRef, role: "alert", tabIndex: -1, children: error })), _jsxs(Form, { onSubmit: submitPassword, noValidate: true, children: [_jsxs(Field, { children: [_jsx(Label, { htmlFor: "admin-identifier", children: t('admin.auth.identifier') }), _jsx(Input, { ref: identifierRef, id: "admin-identifier", name: "identifier", type: "text", autoComplete: "username", required: true, maxLength: 254, value: identifier, onChange: (event) => setIdentifier(event.target.value), disabled: busy })] }), _jsxs(Field, { children: [_jsx(Label, { htmlFor: "admin-password", children: t('admin.auth.password') }), _jsx(Input, { id: "admin-password", name: "password", type: "password", autoComplete: "current-password", required: true, value: password, onChange: (event) => setPassword(event.target.value), disabled: busy })] }), _jsx(PrimaryButton, { type: "submit", disabled: busy || !identifier || !password, children: busy ? t('admin.auth.verifying') : t('admin.auth.continue') })] })] })), phase === 'second-factor' && (_jsxs(Card, { children: [_jsx(Eyebrow, { children: t('admin.auth.secondFactor') }), _jsx(Title, { children: t(recoveryMode ? 'admin.auth.recoveryTitle' : 'admin.auth.authenticatorTitle') }), _jsx(Lead, { children: t(recoveryMode ? 'admin.auth.recoveryLead' : 'admin.auth.authenticatorLead') }), error && (_jsx(ErrorMessage, { ref: errorRef, role: "alert", tabIndex: -1, children: error })), _jsxs(Form, { onSubmit: submitSecondFactor, noValidate: true, children: [recoveryMode ? (_jsxs(Field, { children: [_jsx(Label, { htmlFor: "admin-recovery", children: t('admin.auth.recoveryCode') }), _jsx(Input, { ref: recoveryRef, id: "admin-recovery", name: "recoveryCode", type: "text", autoComplete: "one-time-code", spellCheck: false, required: true, maxLength: 48, value: recoveryCode, onChange: (event) => setRecoveryCode(event.target.value), disabled: busy })] })) : (_jsxs(Field, { children: [_jsx(Label, { htmlFor: "admin-totp", children: t('admin.auth.sixDigitCode') }), _jsx(Input, { ref: totpRef, id: "admin-totp", name: "totp", type: "text", inputMode: "numeric", autoComplete: "one-time-code", pattern: "[0-9]{6}", required: true, maxLength: 6, value: totp, onChange: (event) => setTotp(event.target.value.replace(/\D/g, '').slice(0, 6)), disabled: busy })] })), _jsx(PrimaryButton, { type: "submit", disabled: busy || (recoveryMode ? !recoveryCode : totp.length !== 6), children: busy ? t('admin.auth.verifying') : t('admin.auth.access') }), _jsx(TextButton, { type: "button", onClick: () => {
                                            setError('');
                                            setRecoveryMode((current) => !current);
                                        }, disabled: busy, children: t(recoveryMode ? 'admin.auth.useAuthenticator' : 'admin.auth.useRecovery') })] })] })), phase === 'authenticated' && user && (isCustomers ? (_jsx(AdminCustomersPanel, { role: user.role })) : isDownloads && user.role !== 'read_only' ? (_jsx(AdminDownloadsPanel, { role: user.role })) : (_jsxs(DashboardCard, { children: [_jsxs(DashboardTop, { children: [_jsxs("div", { children: [_jsx(Eyebrow, { children: t('admin.dashboard.protected') }), _jsx(Title, { children: t('admin.dashboard.greeting', { name: user.displayName }) })] }), _jsx(RoleBadge, { children: t(`admin.roles.${user.role}`) })] }), _jsx(DashboardCopy, { children: t('admin.dashboard.intro') }), _jsxs(Details, { children: [_jsx("dt", { children: t('admin.dashboard.role') }), _jsx("dd", { children: t(`admin.roles.${user.role}`) }), _jsx("dt", { children: t('admin.dashboard.lastLogin') }), _jsx("dd", { children: formatLastLogin(user.lastLoginAt, i18n.language, t('admin.dashboard.firstLogin'), t('admin.dashboard.unavailable')) })] }), error && (_jsx(ErrorMessage, { ref: errorRef, role: "alert", tabIndex: -1, children: error })), _jsx(DashboardAction, { to: "/admin/customers", children: t('admin.dashboard.openCustomers') })] })))] })] }));
};
const formatLastLogin = (value, locale, firstLogin, unavailable) => {
    if (!value)
        return firstLogin;
    const date = new Date(value);
    return Number.isNaN(date.getTime())
        ? unavailable
        : new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(date);
};
const PrivateShell = styled.div `
  min-height: 100vh;
  color: ${({ theme }) => theme.colors.ink};
  background:
    radial-gradient(circle at 12% 15%, rgba(65, 105, 225, 0.12), transparent 34rem),
    ${({ theme }) => theme.colors.surface};
`;
const PrivateHeader = styled.header `
  min-height: 5rem;
  padding: 0.85rem clamp(1rem, 4vw, 3rem);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.line};
  background: rgba(255, 255, 255, 0.94);

  @media (max-width: 42rem) {
    align-items: stretch;
    flex-direction: column;
  }
`;
const Brand = styled.div `
  display: inline-flex;
  align-items: center;
  gap: 0.7rem;
  font-weight: 750;
  letter-spacing: -0.02em;
`;
const BrandMark = styled.span `
  width: 2.25rem;
  height: 2.25rem;
  display: grid;
  place-items: center;
  border-radius: 0.7rem;
  color: white;
  background: ${({ theme }) => theme.colors.blue600};
  font-size: 0.8rem;
`;
const AreaLabel = styled.span `
  color: ${({ theme }) => theme.colors.muted};
  font-size: 0.88rem;
  text-align: right;
`;
const AdminNavigation = styled.nav `
  display: flex;
  align-items: center;
  gap: 0.35rem;

  @media (max-width: 42rem) {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;
const AdminNavLink = styled(NavLink) `
  min-height: 2.5rem;
  padding: 0.55rem 0.8rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.55rem;
  color: ${({ theme }) => theme.colors.muted};
  font-size: 0.88rem;
  font-weight: 700;
  text-decoration: none;

  &.active {
    color: ${({ theme }) => theme.colors.blue600};
    background: rgba(65, 105, 225, 0.1);
  }

  &:focus-visible {
    outline: 3px solid rgba(65, 105, 225, 0.25);
  }
`;
const LogoutButton = styled.button `
  min-height: 2.5rem;
  padding: 0.55rem 0.8rem;
  border: 1px solid ${({ theme }) => theme.colors.line};
  border-radius: 0.55rem;
  color: ${({ theme }) => theme.colors.ink};
  background: white;
  font: inherit;
  font-size: 0.88rem;
  font-weight: 700;
  cursor: pointer;
`;
const Content = styled.main `
  min-height: calc(100vh - 5rem);
  width: 100%;
  padding: ${({ $wide }) => ($wide ? 'clamp(1.5rem, 4vw, 3rem)' : 'clamp(2rem, 8vh, 6rem) 1rem')};
  display: grid;
  place-items: start center;
`;
const Card = styled.section `
  width: min(100%, 30rem);
  padding: clamp(1.5rem, 5vw, 2.5rem);
  border: 1px solid ${({ theme }) => theme.colors.line};
  border-radius: 1.25rem;
  background: white;
  box-shadow: 0 1.5rem 4rem rgba(19, 36, 66, 0.1);
`;
const DashboardCard = styled(Card) `
  width: min(100%, 42rem);
`;
const Eyebrow = styled.p `
  margin: 0 0 0.65rem;
  color: ${({ theme }) => theme.colors.blue600};
  font-size: 0.75rem;
  font-weight: 750;
  letter-spacing: 0.11em;
  text-transform: uppercase;
`;
const Title = styled.h1 `
  margin: 0;
  font-size: clamp(1.8rem, 6vw, 2.6rem);
  line-height: 1.08;
`;
const Lead = styled.p `
  margin: 1rem 0 1.6rem;
  color: ${({ theme }) => theme.colors.muted};
  line-height: 1.6;
`;
const Form = styled.form `
  display: grid;
  gap: 1.1rem;
`;
const Field = styled.div `
  display: grid;
  gap: 0.45rem;
`;
const Label = styled.label `
  font-size: 0.9rem;
  font-weight: 650;
`;
const Input = styled.input `
  width: 100%;
  min-height: 3rem;
  padding: 0.7rem 0.85rem;
  border: 1px solid ${({ theme }) => theme.colors.line};
  border-radius: 0.65rem;
  color: ${({ theme }) => theme.colors.ink};
  background: white;
  font: inherit;

  &:focus-visible {
    outline: 3px solid rgba(65, 105, 225, 0.25);
    border-color: ${({ theme }) => theme.colors.blue600};
  }
`;
const PrimaryButton = styled.button `
  min-height: 3rem;
  padding: 0.75rem 1rem;
  border: 0;
  border-radius: 0.65rem;
  color: white;
  background: ${({ theme }) => theme.colors.blue600};
  font: inherit;
  font-weight: 700;
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.58;
  }

  &:focus-visible {
    outline: 3px solid rgba(65, 105, 225, 0.3);
    outline-offset: 2px;
  }
`;
const TextButton = styled.button `
  padding: 0.4rem;
  border: 0;
  color: ${({ theme }) => theme.colors.blue600};
  background: transparent;
  font: inherit;
  text-decoration: underline;
  cursor: pointer;
`;
const ErrorMessage = styled.div `
  margin: 1rem 0;
  padding: 0.85rem 1rem;
  border-left: 3px solid ${({ theme }) => theme.colors.danger};
  border-radius: 0.4rem;
  color: #751c1c;
  background: #fff1f1;
`;
const DashboardTop = styled.div `
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;

  @media (max-width: 34rem) {
    flex-direction: column;
  }
`;
const RoleBadge = styled.span `
  padding: 0.45rem 0.7rem;
  border-radius: 999px;
  color: ${({ theme }) => theme.colors.blue600};
  background: rgba(65, 105, 225, 0.1);
  font-size: 0.78rem;
  font-weight: 750;
  white-space: nowrap;
`;
const DashboardCopy = styled.p `
  margin: 1.5rem 0;
  color: ${({ theme }) => theme.colors.muted};
  line-height: 1.65;
`;
const Details = styled.dl `
  margin: 0 0 1.5rem;
  padding: 1rem;
  display: grid;
  grid-template-columns: minmax(7rem, 0.5fr) 1fr;
  gap: 0.65rem 1rem;
  border-radius: 0.75rem;
  background: ${({ theme }) => theme.colors.surface};

  dt {
    color: ${({ theme }) => theme.colors.muted};
  }

  dd {
    margin: 0;
    font-weight: 650;
  }

  @media (max-width: 28rem) {
    grid-template-columns: 1fr;
    gap: 0.25rem;

    dd + dt {
      margin-top: 0.65rem;
    }
  }
`;
const DashboardAction = styled(NavLink) `
  min-height: 3rem;
  padding: 0.75rem 1rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.65rem;
  color: white;
  background: ${({ theme }) => theme.colors.blue600};
  font-weight: 700;
  text-decoration: none;
`;
